import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Tile } from "@fawkes/ui";

import { formatDate } from "../i18n/format";
import "./NazorgTile.css";

// Nazorg-dier migration (Track B): the SPA port of the hybrid nazorg-dier React app
// (frontend-react/nazorg-dier — Jmix AnimalNazorgTileFragment/NazorgDierHost, Epic 28). The old
// postMessage bridge (nazorg-dier:ready/data/actie{verstuur}/link/modal) is replaced by the api:
//   GET  /api/animals/{dierId}/nazorg             → dossier payload (incl. heeftAfgehandeldVerblijf)
//   POST /api/animals/{dierId}/nazorg/uitnodiging → issue invitation, returns the adopter link
// De tegel zelf (`view=tegel`: welzijnsscore + 5 dimensiebalken + laatste signaal + versturen/dossier)
// staat op de Verblijf-tab van het dier. De samenvatting die hier eerst in een venster openschoof is
// eruit: "Bekijk dossier" opent nu het nazorgdossier zelf, het scherm met de gegeven antwoorden.
//
// Visibility mirrors Jmix (AnimalStayTabFragment): the tile only shows for post-departure animals
// (heeftAfgehandeldVerblijf) and while the nazorg module is on (the module gate lives in
// AnimalDetail, like the taken tile). Timeline titles/labels come from the server (Dutch, parity
// with the Jmix payload) — the client only translates its own chrome via the `nazorg.*` i18n family.

// ── wire types (see api NazorgDossierModels) ────────────────────────────────────────────────────────
export interface NazorgDierContext {
  roepnaam: string | null;
  diersoort: string | null;
  ras: string | null;
}

export interface NazorgWelzijnsscore {
  totaal: number | null;
  bekend: boolean;
  bijgewerktOp: string | null;
  dimensies: Record<string, number | null>;
}

export interface NazorgTijdlijnItem {
  type: string;
  titel: string | null;
  omschrijving: string | null;
  prioriteit: string | null;
  contactmoment: string | null;
  datum: string | null;
}

export interface NazorgOpenContactmoment {
  contactmoment: string | null;
  contactmomentLabel: string | null;
  status: string | null;
  verstuurdOp: string | null;
  verlooptOp: string | null;
}

export interface NazorgSamenvatting {
  aantalInzendingen: number;
  laatsteInzending: {
    inzendingId: number | null;
    prioriteit: string | null;
    snippet: string | null;
    ingediendOp: string | null;
  } | null;
}

export interface NazorgDossier {
  dierId: number;
  heeftAfgehandeldVerblijf: boolean;
  dier: NazorgDierContext;
  welzijnsscore: NazorgWelzijnsscore;
  tijdlijn: NazorgTijdlijnItem[];
  open: NazorgOpenContactmoment[];
  samenvatting: NazorgSamenvatting;
}

// ── presentation helpers (hybrid parity) ────────────────────────────────────────────────────────────
// Status colour for a 0..100 score; null = grey (hybrid scoreStatus).
export function scoreStatus(v: number | null | undefined): "groen" | "oranje" | "rood" | "grijs" {
  if (v === null || v === undefined) return "grijs";
  if (v >= 67) return "groen";
  if (v >= 34) return "oranje";
  return "rood";
}

const DIM_VOLGORDE = ["VOEDING", "GEDRAG", "GEZONDHEID", "HECHTING", "STRESS"] as const;
// Source DIM_ICONS + SEGMENTEN — the redesigned tile shows a 4-segment bar per dimension.
const DIM_ICONS: Record<string, string> = {
  VOEDING: "🥣",
  GEDRAG: "🐕",
  GEZONDHEID: "🩺",
  HECHTING: "❤️",
  STRESS: "⛈️",
};
const SEGMENTEN = 4;

/**
 * The tile's aggregate block, derived client-side from the dossier the api already returns (the Jmix
 * `tegelBlok` was a server-side aggregation; there's no need to add it to the payload). `telling` counts
 * the submissions by priority colour; the contact dates and snippet come from the summary/open moments.
 */
function afleidenTegel(dossier: NazorgDossier) {
  const inzendingen = dossier.tijdlijn.filter((it) => it.type === "inzending");
  const telling = { groen: 0, oranje: 0, rood: 0, totaal: 0 };
  for (const it of inzendingen) {
    if (it.prioriteit === "rood") telling.rood++;
    else if (it.prioriteit === "oranje") telling.oranje++;
    else if (it.prioriteit === "groen") telling.groen++;
    else continue;
    telling.totaal++;
  }
  // Next open contact moment: the soonest expiry among open invitations.
  const volgende = dossier.open
    .map((o) => o.verlooptOp)
    .filter((d): d is string => !!d)
    .sort()[0];
  return {
    actief: dossier.samenvatting.aantalInzendingen > 0 || dossier.open.length > 0,
    laatsteContact: dossier.samenvatting.laatsteInzending?.ingediendOp ?? null,
    volgendeMoment: volgende ?? null,
    snippet: dossier.samenvatting.laatsteInzending?.snippet ?? null,
    telling,
  };
}

/** Ring showing groen/totaal (source TegelRing). */
function TegelRing({ groen, totaal }: { groen: number; totaal: number }) {
  const R = 42;
  const omtrek = 2 * Math.PI * R;
  const pct = totaal > 0 ? groen / totaal : 0;
  return (
    <svg className="fawkes-nazorg-dier__ring" viewBox="0 0 100 100" role="img" aria-label={`${groen} van ${totaal} goed`}>
      <circle className="fawkes-nazorg-dier__ringspoor" cx="50" cy="50" r={R} />
      <circle
        className="fawkes-nazorg-dier__ringvulling"
        cx="50"
        cy="50"
        r={R}
        strokeDasharray={`${omtrek * pct} ${omtrek}`}
        transform="rotate(-90 50 50)"
      />
      <text x="50" y="46" className="fawkes-nazorg-dier__ringtekst">
        {groen}
        <tspan className="fawkes-nazorg-dier__ringtotaal"> / {totaal}</tspan>
      </text>
      <text x="50" y="66" className="fawkes-nazorg-dier__ringhart">♡</text>
    </svg>
  );
}

/** One dimension row with a 4-segment bar (source DimRij). */
function DimRij({ dim, label, waarde }: { dim: string; label: string; waarde: number | null }) {
  const status = scoreStatus(waarde);
  const gevuld = waarde == null ? 0 : Math.max(0, Math.min(SEGMENTEN, Math.round((waarde / 100) * SEGMENTEN)));
  return (
    <div className="fawkes-nazorg-dier__dimrij">
      <span className="fawkes-nazorg-dier__dimicon" aria-hidden="true">{DIM_ICONS[dim] ?? "•"}</span>
      <span className="fawkes-nazorg-dier__dimlabel">{label}</span>
      <span className={`fawkes-nazorg-dier__dimseg fawkes-nazorg-dier__dimseg--${status}`}>
        {Array.from({ length: SEGMENTEN }).map((_, i) => (
          <i key={i} className={`fawkes-nazorg-dier__seg${i < gevuld ? " is-aan" : ""}`} />
        ))}
      </span>
      <span className={`fawkes-nazorg-dier__dimtelling fawkes-nazorg-dier__dimtelling--${status}`}>
        {waarde == null ? "—" : `${gevuld} / ${SEGMENTEN}`}
      </span>
    </div>
  );
}

// ── the Verblijf-tab tile (hybrid `view=tegel`) ─────────────────────────────────────────────────────
// Self-gating: renders nothing until the dossier confirms a completed stay (Jmix parity — the tile
// only exists for post-departure animals). The surrounding Tile chrome is rendered here for that
// reason (the other Verblijf tiles are wrapped by AnimalDetail).
export function NazorgVerblijfTile({ animalId, selectedStayId, onOpenInzending }: {
  animalId: string;
  selectedStayId?: string | null;
  /**
   * "Bekijk dossier" opent het nazorgdossier van de laatste inzending — het scherm met de antwoorden.
   * Zo gevraagd: het samenvattingsvenster dat hier eerst opende, wilde hij niet meer zien.
   */
  onOpenInzending?: (inzendingId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, refetch } = useCustom({ url: `animals/${animalId}/nazorg`, method: "get" });
  const dossier = data?.data as NazorgDossier | undefined;

  const { mutate, isLoading: versturen } = useCustomMutation();
  // undefined = not asked yet; null = asked but no published questionnaire; string = the link.
  const [link, setLink] = useState<string | null | undefined>(undefined);
  const [gekopieerd, setGekopieerd] = useState(false);

  // Two different questions, and they must not be conflated:
  //   • may we START aftercare? — the api's heeftAfgehandeldVerblijf: the animal's LAST stay is a
  //     completed PLACEMENT. A TNRC release, a return, or a new stay leaves nobody to write to.
  //   • is there a dossier to READ? — whatever was already sent or filled in. That history stays
  //     visible for good; you just cannot add to it once the placement is over.
  const magVersturen = dossier?.heeftAfgehandeldVerblijf === true;
  const heeftDossier = (dossier?.tijdlijn?.length ?? 0) > 0
    || (dossier?.samenvatting?.aantalInzendingen ?? 0) > 0;
  if (!dossier || (!magVersturen && !heeftDossier)) return null;
  void selectedStayId;

  const wz = dossier.welzijnsscore;
  const tegel = afleidenTegel(dossier);
  const laatsteInzendingId = dossier.samenvatting.laatsteInzending?.inzendingId ?? null;
  const { groen, oranje, rood, totaal } = tegel.telling;
  // Source badge/kop logic: rood > oranje > alles-goed.
  const resultaat =
    totaal === 0
      ? null
      : rood > 0
        ? { kop: t("nazorg.tegel.actieNodig"), badge: t("nazorg.tegel.badge.actieNodig"), cls: "rood" }
        : oranje > 0
          ? { kop: t("nazorg.tegel.overwegendGoed"), badge: t("nazorg.tegel.badge.aandacht"), cls: "oranje" }
          : { kop: t("nazorg.tegel.allesGoed"), badge: t("nazorg.tegel.badge.opKoers"), cls: "groen" };

  const verstuur = () => {
    setLink(undefined);
    mutate(
      {
        url: `animals/${animalId}/nazorg/uitnodiging`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          setLink(((res?.data as { url?: string | null } | undefined)?.url ?? null) as string | null);
          refetch();
        },
        // Jmix parity: a failed issue shows the same "no questionnaire" notice (empty :link message).
        onError: () => setLink(null),
      },
    );
  };

  // The link is long and the field is narrow, so selecting what is visible silently truncates the token
  // (an adopter then lands on "Link niet meer geldig"). Copy the whole value instead — same best-effort
  // shape as the media card's copyLinkToClipboard.
  const kopieerLink = async () => {
    if (typeof link !== "string") {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setGekopieerd(true);
      setTimeout(() => setGekopieerd(false), 1500);
    } catch { /* clipboard unavailable — best-effort, like the media card */ }
  };

  return (
    <Tile title={t("animalDetailView.stay.tile.nazorg")} className="fawkes-nazorg-dier__tile">
      <div className="fawkes-nazorg-dier">
        <div className="fawkes-nazorg-dier__kop">
          <h2 className="fawkes-nazorg-dier__koptitel">{t("nazorg.tile.title")}</h2>
        </div>

        {/* Status banner (source): the 🌱 lives here, not in the title. */}
        <div className={`fawkes-nazorg-dier__banner${tegel.actief ? "" : " fawkes-nazorg-dier__banner--inactief"}`}>
          <span aria-hidden="true">🌱</span>
          {tegel.actief ? t("nazorg.tegel.actief") : t("nazorg.tegel.nietGestart")}
        </div>

        {totaal > 0 && resultaat ? (
          <div className="fawkes-nazorg-dier__ringblok">
            <TegelRing groen={groen} totaal={totaal} />
            <div className="fawkes-nazorg-dier__ringzij">
              <div className="fawkes-nazorg-dier__ringkop">{resultaat.kop}</div>
              <span className={`fawkes-nazorg-dier__statusbadge fawkes-nazorg-dier__statusbadge--${resultaat.cls}`}>
                {resultaat.badge}
              </span>
            </div>
          </div>
        ) : (
          <div className="fawkes-nazorg-dier__scoreblok">
            <span className="fawkes-nazorg-dier__scoreleeg">{t("nazorg.tegel.geenVragenlijst")}</span>
          </div>
        )}

        {(tegel.laatsteContact || tegel.volgendeMoment) && (
          <div className="fawkes-nazorg-dier__contactrijen">
            {tegel.laatsteContact && (
              <div className="fawkes-nazorg-dier__contactrij">
                <span aria-hidden="true">📅</span>
                <span className="fawkes-nazorg-dier__contactlabel">{t("nazorg.tegel.laatsteContact")}:</span>
                <b>{formatDate(tegel.laatsteContact, locale)}</b>
              </div>
            )}
            {tegel.volgendeMoment && (
              <div className="fawkes-nazorg-dier__contactrij">
                <span aria-hidden="true">⏰</span>
                <span className="fawkes-nazorg-dier__contactlabel">{t("nazorg.tegel.volgendeMoment")}:</span>
                <b>{formatDate(tegel.volgendeMoment, locale)}</b>
              </div>
            )}
          </div>
        )}

        {wz.bekend && (
          <div className="fawkes-nazorg-dier__dimlijst">
            {DIM_VOLGORDE.map((k) => (
              <DimRij key={k} dim={k} label={t(`nazorg.dim.${k}`)} waarde={wz.dimensies[k] ?? null} />
            ))}
          </div>
        )}

        {tegel.snippet && (
          <div className="fawkes-nazorg-dier__opmerking">
            <span aria-hidden="true">♡</span>
            <div>
              <div className="fawkes-nazorg-dier__opmerkingkop">{t("nazorg.tegel.opmerking")}</div>
              <div className="fawkes-nazorg-dier__opmerkingtekst">{tegel.snippet}</div>
            </div>
          </div>
        )}

        {totaal > 0 && (
          <div className="fawkes-nazorg-dier__scorepills">
            <span className="fawkes-nazorg-dier__scorepillslabel">{t("nazorg.tegel.laatsteScore")}:</span>
            {groen > 0 && <span className="fawkes-nazorg-dier__scorepill fawkes-nazorg-dier__scorepill--groen">● {t("nazorg.tegel.nGroen").replace("%d", String(groen))}</span>}
            {oranje > 0 && <span className="fawkes-nazorg-dier__scorepill fawkes-nazorg-dier__scorepill--oranje">● {t("nazorg.tegel.nOranje").replace("%d", String(oranje))}</span>}
            {rood > 0 && <span className="fawkes-nazorg-dier__scorepill fawkes-nazorg-dier__scorepill--rood">● {t("nazorg.tegel.nRood").replace("%d", String(rood))}</span>}
          </div>
        )}

        <div className="fawkes-nazorg-dier__acties">
          {/* Read-only once the placement is behind us: the dossier stays open, sending does not. */}
          {magVersturen && (
            <Button variant="primary" onClick={verstuur} disabled={versturen}>
              {t("nazorg.tile.verstuur")}
            </Button>
          )}
          {/* Altijd zichtbaar, zodat je weet waar de antwoorden van de adoptant komen te staan (Alma, Roze,
              2026-10-09: "hoe zie ik de nazorgantwoorden terug?"). Te openen zodra er iets is ingevuld. */}
          {onOpenInzending && (
            <Button
              variant="secondary"
              disabled={laatsteInzendingId == null}
              title={laatsteInzendingId == null ? t("nazorg.tile.nogGeenAntwoorden") : undefined}
              onClick={() => laatsteInzendingId != null && onOpenInzending(String(laatsteInzendingId))}
            >
              {t("nazorg.tile.dossier")}
            </Button>
          )}
        </div>
        {onOpenInzending && laatsteInzendingId == null && (
          <div className="fawkes-nazorg-dier__linkmelding">{t("nazorg.tile.nogGeenAntwoorden")}</div>
        )}
        {!magVersturen && (
          <div className="fawkes-nazorg-dier__linkmelding">{t("nazorg.tile.alleenLezen")}</div>
        )}

        {typeof link === "string" && (
          <div className="fawkes-nazorg-dier__linkblok">
            <div className="fawkes-nazorg-dier__linklabel">{t("nazorg.tile.linkLabel")}</div>
            <div className="fawkes-nazorg-dier__linkrij">
              <input
                className="fawkes-nazorg-dier__linkinput"
                readOnly
                value={link}
                aria-label={t("nazorg.tile.linkLabel")}
                onFocus={(e) => e.target.select()}
              />
              <Button variant="secondary" onClick={() => void kopieerLink()}>
                {t(gekopieerd ? "nazorg.tile.linkGekopieerd" : "nazorg.tile.linkKopieer")}
              </Button>
            </div>
          </div>
        )}
        {link === null && <div className="fawkes-nazorg-dier__linkmelding">{t("nazorg.tile.linkLeeg")}</div>}

      </div>
    </Tile>
  );
}
